import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './scss/Attachment.scss';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { fetchCardById } from '../../redux/Slices/cardSlice';

import { fetchAttachments, uploadAttachment } from '../../redux/Slices/attachmentSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { showUploading, completeUploadProgress } from '../../utils/snackbarHelpers';
import { INSERT_IMAGE_COMMAND } from '../LexicalEditor/plugins/ImagesPlugin';

import {
    $getSelection,
    $createTextNode,
    $insertNodes
} from "lexical";

import { LinkNode } from '@lexical/link';
import { config } from '../../config';
import { isValidFile, isImage } from '../../utils/helpers';
import Close from '../icons/Close';

const Attachment = ({ type, editor }) => {
    const [title, setTitle] = useState('Attachment');
    const [attachmentUrl, setAttachmentUrl] = useState('');
    const [isUrlValid, setIsUrlValid] = useState(true);
    const [isTextareaFocused, setIsTextareaFocused] = useState(false); // New focus state
    const { status, attachments } = useSelector((state) => state.attachments);

    const urlRef = useRef(null);
    const fileInputRef = useRef(null);

    const { card } = useSelector((state) => state.card);
    const cardId = card.shortId;
    const dispatch = useDispatch();

    // Set title based on type
    useEffect(() => {
        setTitle(type === 'IMAGE' ? 'Image' : type === 'COVER' ? 'Cover' : 'Attachment');
    }, [type]);


    useEffect(() => {
        if (urlRef.current) {
            urlRef.current.style.height = '22px';
            urlRef.current.style.height = `${urlRef.current.scrollHeight}px`;
        }
    }, [attachmentUrl]);

    // Fetch attachments when component mounts or cardId changes
    useEffect(() => {
        if (cardId) {
            dispatch(fetchAttachments(cardId));
        }
    }, [cardId, dispatch]);

    useEffect(() => {
        if (status === 'uploadSucceeded') {
            dispatch(fetchAttachments(cardId));
        }
    }, [status, dispatch, cardId]);

    // Image URL validation function
    const validateImageUrl = (url) => {
        const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
        return imageExtensions.test(url);
    };

    const handleUrlChange = (e) => {
        const url = e.target.value;
        setAttachmentUrl(url);
        setIsUrlValid(validateImageUrl(url));
    };

    const fetchAttachment = async (cardId, attachmentId) => {


        try {
            const response = await fetch(`${config.API_URI}/v1/cards/attachments/${cardId}/${attachmentId}`, {
                method: 'GET',
                headers: {

                },
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Failed to fetch attachment');
            }

            const data = await response.json();

            setAttachmentUrl(data.attachment?.url);

            return data.attachment; // Return the fetched attachment data
        } catch (error) {
            console.error('Error fetching attachment:', error);
            throw error; // Re-throw the error for further handling if needed
        }
    };

    const handleFileUpload = async (e) => {
        try {
            const file = e.target.files[0];
            if (!file) return;

            // Validate file type and size
            const validation = isValidFile(file);
            if (!validation.valid) {
                return dispatch(showSnackBar({ message: validation.error, type: "error" }));
            }

            // Start upload progress tracking
            const uploadId = showUploading(dispatch, "Uploading files...");

            // Set global upload ID for WebSocket progress tracking
            window.currentUploadId = uploadId;

            const response = await dispatch(uploadAttachment({
                file,
                cardId,
                type,
                uploadId
            }));

            // Clear global upload ID
            window.currentUploadId = null;

            if (response.meta.requestStatus !== "fulfilled") {
                throw new Error(response.payload?.message || "File upload failed.");
            }

            // Complete upload with proper progress display
            completeUploadProgress(dispatch, uploadId);

            const { attachment } = response.payload;
            const fetchedUrl = (await fetchAttachment(cardId, attachment?._id)).url;

            if (editor) {
                file.type.startsWith("image/")
                    ? insertImageInEditor(fetchedUrl, file.name)
                    : insertAttachmentLink(fetchedUrl, file.name);
            }

            dispatch(fetchCardById(cardId));
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));

        } catch (error) {
            console.error("File upload error:", error);

            // Clear global upload ID on error
            window.currentUploadId = null;
            dispatch(showSnackBar({ message: error.message || "An error occurred while uploading the file.", type: "error" }));
        }
    };



    const insertAttachment = (attachmentUrl) => {
        if (validateImageUrl(attachmentUrl)) {
            insertImageInEditor(attachmentUrl);
        } else {
            const fileName = attachmentUrl.split('/').pop();
            insertAttachmentLink(attachmentUrl, fileName);
        }
    };

    // Remove cover from card
    const removeCover = async () => {
        try {
            const response = await fetch(`${config.API_URI}/v1/cards/update/${cardId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ cover: null })
            });

            if (!response.ok) {
                throw new Error('Failed to remove cover');
            }

            dispatch(fetchCardById(cardId));
            dispatch(showSnackBar({ message: "Cover removed successfully", type: "success" }));
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
        } catch (error) {
            console.error('Error removing cover:', error);
            dispatch(showSnackBar({ message: error.message || "Failed to remove cover", type: "error" }));
        }
    };

    // Set an existing attachment as cover
    const setAttachmentAsCover = async (attachment) => {
        try {
            const response = await fetch(`${config.API_URI}/v1/cards/update/${cardId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    cover: {
                        name: attachment.name,
                        url: attachment.url,
                        attachmentId: attachment._id,
                        fileHash: attachment.fileHash
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to set cover');
            }

            dispatch(fetchCardById(cardId));
            dispatch(showSnackBar({ message: "Cover set successfully", type: "success" }));
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
        } catch (error) {
            console.error('Error setting cover:', error);
            dispatch(showSnackBar({ message: error.message || "Failed to set cover", type: "error" }));
        }
    };

    // Insert image node into the editor
    const insertImageInEditor = (imageUrl, fileName) => {
        const payload = {
            altText: fileName || "Uploaded Image",
            src: imageUrl,
        };
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);

        dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));

    };

    // Insert link to attachment into the editor
    const insertAttachmentLink = (fileUrl, fileName) => {
        editor?.update(() => {
            const selection = $getSelection();
            if (selection) {
                // Create a text node for the file name
                const textNode = $createTextNode(fileName);

                // Create a link node and set its URL
                const linkNode = new LinkNode(fileUrl);
                linkNode.append(textNode);

                $insertNodes([linkNode]);

                dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
            }
        });
    };


    const attachmentAcceptType = type === 'IMAGE' || type === 'COVER' ? 'image/*' : 'file';


    return (
        <div className="add-attachment">
            <h2>{title}</h2>

            {/* <div className="group">
                <h3>{type === 'IMAGE' ? 'Image URL' : type === 'COVER' ? 'Cover URL' : 'Attach URL'}</h3>
                <div className={`textarea-wrapper ${isTextareaFocused ? 'focused' : ''}`}>
                    <textarea
                        ref={urlRef}
                        type="url"
                        placeholder={type === 'IMAGE' ? 'Enter image URL' : type === 'COVER' ? 'Enter cover URL' : 'Enter attachment URL'}
                        value={attachmentUrl}
                        onChange={handleUrlChange}
                        rows={1}
                        style={{ overflow: 'hidden' }}
                        spellCheck={false}
                        onFocus={() => setIsTextareaFocused(true)}
                        onBlur={() => setIsTextareaFocused(false)}
                    />
                </div>
                {type === 'IMAGE' && !isUrlValid && <p className="error-message">Please enter a valid image URL (e.g., .bmp, .gif, .jpg, .jpeg, .png, or .webp).</p>}
                <button onClick={() => insertAttachment(attachmentUrl)} disabled={type === 'IMAGE' && !isUrlValid}>Submit</button>
            </div> */}

            <div className="group">
                <h3>{type === 'IMAGE' ? 'or Upload an image from your computer' : type === 'COVER' ? 'or Upload a cover image from your computer' : 'or Attach a file from your computer'}</h3>
                <button onClick={() => fileInputRef.current.click()} className='attach-file-btn'>
                    {status === 'loading' ? 'Uploading...' :
                        status === 'uploadSucceeded' ? 'Upload Successful' :
                            type === 'IMAGE' ? 'Choose an image' : type === 'COVER' ? 'Choose a cover image' : 'Choose a file'
                    } (max 20mb)
                </button>

                <input
                    type="file"
                    accept={attachmentAcceptType}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                />
            </div>

            {/* Remove Cover Option - Only show for COVER type and when card has a cover */}
            {type === 'COVER' && card?.cover?.url && (
                <div className="group">
                    <p>Remove the current cover image from this card.</p>
                    <button onClick={removeCover} className="remove-cover-btn">
                        <Close color='var(--button-background-color)' size={16} />
                        Remove Cover
                    </button>
                </div>
            )}

            {/* Uploaded Attachments - Only show for IMAGE and COVER types */}
            {(type === 'IMAGE' || type === 'COVER') && attachments && attachments.length > 0 && (
                <div className="group">
                    <h3>
                        {type === 'COVER' ? 'Choose from uploaded images' : 'Choose from uploaded attachments'}
                    </h3>

                    <div className="uploaded-attachments-grid">
                        {attachments
                            .filter((attachment) => attachment?.fileType === 'IMAGE')
                            .slice(-6) // last 6 images
                            .map((attachment) => (
                                <div
                                    key={attachment._id}
                                    className="attachment-item"
                                    onClick={() => {
                                        setAttachmentAsCover(attachment);
                                    }}
                                >
                                    <div className="attachment-preview">
                                        <img
                                            src={attachment.url}
                                            alt={attachment.name}
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="attachment-name">
                                        {attachment.name}
                                    </div>
                                </div>
                            ))}

                    </div>
                </div>
            )}
        </div>
    );

};

export default Attachment;
